import { jsx as _jsx } from "@emotion/react/jsx-runtime";
import { css } from '@emotion/react';
import { memo, useCallback, useEffect, useMemo, useRef, useState, } from 'react';
import styleUtils from '../../styles/styleUtils';
import theme from '../../styles/theme';
const defaultProps = {
    gridColumnMinimumWidth: 100,
    layout: 'stack',
    stackLayout: 'wrap',
};
const SelectorDisplay = ({ children, gridColumnMinimumWidth = defaultProps.gridColumnMinimumWidth, horizontalPadding = 0, layout = defaultProps.layout, stackLayout = defaultProps.stackLayout, }) => {
    const containerRef = useRef(null);
    const [isRightOverlayVisible, setIsRightOverlayVisible,] = useState(true);
    const [isLeftOverlayVisible, setIsLeftOverlayVisible,] = useState(false);
    const containerStyles = useMemo(() => (css `
        height: 100%;
        max-width: 100%;

        ${layout === 'scroll' && css `
          flex-wrap: nowrap;
          overflow: hidden;
          position: relative;

          &::after {
            background:
              linear-gradient(to right, transparent 90%, ${isRightOverlayVisible ? theme.colors.gray100 : 'transparent'}),
              linear-gradient(to left, transparent 90%, ${isLeftOverlayVisible ? theme.colors.gray100 : 'transparent'});
            bottom: 0;
            content: '';
            left: ${isLeftOverlayVisible ? '0' : 'auto'};
            opacity: ${isLeftOverlayVisible || isRightOverlayVisible ? 1 : 0};
            pointer-events: none;
            position: absolute;
            right: ${isRightOverlayVisible ? '0' : 'auto'};
            top: 0;
            width: 100%;
          }
        `}
      `), [
        isLeftOverlayVisible,
        isRightOverlayVisible,
        layout,
    ]);
    const contentStyles = useMemo(() => (css `
        column-gap: ${theme.spacing.x2};
        display: flex;
        height: 100%;
        padding-left: ${styleUtils.rem(horizontalPadding)};
        padding-right: ${styleUtils.rem(horizontalPadding)};
        row-gap: ${theme.spacing.x2};
        white-space: nowrap;

        ${layout === 'scroll' && css `
          ${styleUtils.visibleScrollbarStyles}
          flex-wrap: nowrap;
          overflow-x: auto;
          overflow-y: hidden;
          padding-bottom: ${theme.spacing.base};
          position: relative;

          @media (pointer: coarse) {
            ${styleUtils.hiddenScrollbarStyles}
          }
        `}

        ${layout === 'stack' && stackLayout === 'wrap' && css `
          flex-wrap: wrap;
        `}

        ${layout === 'stack' && stackLayout === 'grid' && css `
          display: grid;
          grid-template-columns: repeat(
            auto-fill,
            minmax(${styleUtils.rem(gridColumnMinimumWidth)}, 1fr)
          );
        `}
    `), [
        gridColumnMinimumWidth,
        horizontalPadding,
        layout,
        stackLayout,
    ]);
    const onScrollCapture = useCallback(() => {
        if (containerRef.current && layout === 'scroll') {
            const { clientWidth, scrollLeft, scrollWidth, } = containerRef.current;
            setIsLeftOverlayVisible(scrollLeft > 0);
            setIsRightOverlayVisible(scrollLeft < scrollWidth - clientWidth);
        }
    }, [
        layout,
    ]);
    useEffect(() => {
        if (!(containerRef.current && layout === 'scroll')) {
            return;
        }
        let requestAnimationFrameId;
        const callback = () => {
            cancelAnimationFrame(requestAnimationFrameId);
            requestAnimationFrameId = (window
                .requestAnimationFrame(() => {
                onScrollCapture();
            }));
        };
        const resizeObserver = new ResizeObserver(callback);
        resizeObserver.observe(containerRef.current);
        return () => {
            resizeObserver.disconnect();
        };
    }, [
        layout,
        onScrollCapture,
    ]);
    return (_jsx("div", Object.assign({ css: containerStyles }, { children: _jsx("div", Object.assign({ css: contentStyles, onScrollCapture: onScrollCapture, ref: containerRef }, { children: children })) })));
};
const MemoizedSelectorDisplay = memo(SelectorDisplay);
export { MemoizedSelectorDisplay as SelectorDisplay };
// MC-1068 Temporary hack for Storybook.
export default SelectorDisplay;
